import { DiseaseCategoryType, SortType } from "fe-common/graphql";
import { MFindingGroup } from "./MFindingGroup";

/**
 * Convert prob to fraction of numerator divided by denominator as array [numerator, denumenator]
 * Examples: asFraction(0.05214, 6, true, 1) => [5.2, 100]
  *          asFraction(0.05214, 6, true, 2) => [5.21, 100]
  *         asFraction(0.05214, 6, false, 1) => [1, 10]
 *          asFraction(0.05214, 1, false, 1) => [Null, Null]
 *
 *
 * @param prob given prob
 * @param maxdigits max number of digits to consider for the fraction
 * @param noround if true, do not round the fraction
 * @param maxNumeratorDecimalPlaces max number of decimal places of numerator to round to
 */
export function asFraction(
  prob: number,
  maxdigits = 4,
  noround = false,
  maxNumeratorDecimalPlaces = 2
): [number, number] {
  if (prob > 1.5) {
    return [Math.round(prob), null];
  }

  let maxdenom = Math.pow(10, maxdigits);
  if (!noround && prob < 1 / maxdenom) {
    return [null, null];
  }

  let denom, numer;

  for (denom = 10; denom <= maxdenom; denom = denom * 10) {
    numer = prob * denom;
    let rounded = noround ? numer : Math.round(numer);
    // if (rounded > 0 && Math.abs(rounded - numer) < 0.4) break;
    if (rounded >= 1) {
      break;
    }
  }
  return [noround ? roundFixed(numer, maxNumeratorDecimalPlaces) : Math.round(numer), denom];
}

/**
 * round to fixed decimal places (default 2)
 * Examples: roundFixed(1.23456789, 2) => 1.23 ,
 *           roundFixed(1.23456789, 0) => 1
 *           roundFixed(0.146533, 1) => 0.1
 * @param number to round
 * @param decimalPlaces number of decimal places to round to
 */
function roundFixed (number, decimalPlaces = 2) {
  const factorOfTen = Math.pow(10, decimalPlaces);
  return Math.round(number * factorOfTen) / factorOfTen;
}

export function nameDisease(d) {
  return d && d.name;
}

const sortField = (i: MFindingGroup, sort: SortType) =>
  sort == SortType.Prob
    ? i.maxFindingGivenDisease || i.findingGivenDisease
    : i.maxAbsolutePredictiveValue || i.absolutePredictiveValue;
export const sortFindingGroup = (sort: SortType) => (a, b) =>
  sortField(b, sort) - sortField(a, sort);

export let categories: Array<[DiseaseCategoryType, string]> = [
  [DiseaseCategoryType.PatientFinding, "Patient"],
  [DiseaseCategoryType.Population, "Epidemiology"],
  [DiseaseCategoryType.Risks, "Risk Factors"],
  [DiseaseCategoryType.Etiology, "Etiology"],
  [DiseaseCategoryType.OutcomeSigns, "Clinical Features"],
  [DiseaseCategoryType.OutcomeComplication, "Complications"],
  [DiseaseCategoryType.OutcomeTest, "Labs"],
  [DiseaseCategoryType.Imaging, "Diagnostics"],
  [DiseaseCategoryType.Association, "Associated"],
  [DiseaseCategoryType.Other, "Other"],
];

export const isDiseaseCategoryType = (v: any) =>
  Object.values(DiseaseCategoryType).includes(v);

// Isn't this redundant to the tabName function in ask/src/assets/tabs?
export const categoryName = (c: DiseaseCategoryType) =>
  (categories.find((entry) => entry[0] === c) || [null, null])[1];

export const categoryTypeByName = (c: string) =>
  (categories.find((entry) => entry[1] === c) || [null, null])[0];

export const DefaultSort: Partial<Record<DiseaseCategoryType, SortType>> = {
  [DiseaseCategoryType.OutcomeComplication]: SortType.Prob,
  [DiseaseCategoryType.OutcomeSigns]: SortType.Prob,
  [DiseaseCategoryType.Association]: SortType.Prob,
  [DiseaseCategoryType.Population]: SortType.BestMatch,
  [DiseaseCategoryType.Etiology]: null,
};
